import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCallback } from 'react';

const UTMContext = createContext();

// UTM keys to track
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
const ALL_KEYS = [...UTM_KEYS, 'gclid'];

// Helper to get UTMs from URL
const getUTMsFromURL = () => {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const utms = {};
  
  ALL_KEYS.forEach(key => {
    const value = params.get(key);
    if (value && value !== '') {
      utms[key] = value;
    }
  });
  
  return utms;
};

// Helper to get UTMs from sessionStorage
const getUTMsFromSession = () => {
  if (typeof sessionStorage === 'undefined') return {};
  
  const utms = {};
  ALL_KEYS.forEach(key => {
    const value = sessionStorage.getItem(key);
    if (value && value !== '' && value !== '(direct)' && value !== '(none)') {
      utms[key] = value;
    }
  });
  
  return utms;
};

// Helper to check if analytics consent is granted
const hasAnalyticsConsent = () => {
  if (typeof window === 'undefined') return false;
  try {
    const savedConsent = localStorage.getItem('gtm_consent');
    if (savedConsent) {
      const consent = JSON.parse(savedConsent);
      return consent.analytics === true;
    }
  } catch (e) {
    // Ignore
  }
  return false;
};

export const UTMProvider = ({ children }) => {
  const location = useLocation();
  
  // ============================================================
  // Initialize UTMs from URL AND sessionStorage
  // ============================================================
  const [utmParams, setUtmParams] = useState(() => {
    const urlUtms = getUTMsFromURL();
    const sessionUtms = getUTMsFromSession();
    
    // Merge: URL takes precedence, but sessionStorage fills gaps
    const mergedUtms = { ...sessionUtms, ...urlUtms };
    
    if (Object.keys(mergedUtms).length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[UTM] Initial UTMs (URL + session):', mergedUtms);
      }
      return {
        utm_source: mergedUtms.utm_source || '(direct)',
        utm_medium: mergedUtms.utm_medium || '(none)',
        utm_campaign: mergedUtms.utm_campaign || '',
        utm_content: mergedUtms.utm_content || '',
        utm_term: mergedUtms.utm_term || '',
        utm_id: mergedUtms.utm_id || '',
        gclid: mergedUtms.gclid || ''
      };
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[UTM] No UTMs found, using defaults');
    }
    return {
      utm_source: '(direct)',
      utm_medium: '(none)',
      utm_campaign: '',
      utm_content: '',
      utm_term: '',
      utm_id: '',
      gclid: ''
    };
  });

  // Function to capture UTMs from current URL and sessionStorage
  const captureUTMsFromURL = useCallback(() => {
    const urlUtms = getUTMsFromURL();
    const sessionUtms = getUTMsFromSession();
    
    // Merge URL and sessionStorage UTMs
    const mergedUtms = { ...sessionUtms, ...urlUtms };
    
    if (Object.keys(mergedUtms).length > 0) {
      setUtmParams(prev => {
        const hasChanged = ALL_KEYS.some(key => {
          const oldVal = prev[key] || '';
          const newVal = mergedUtms[key] || '';
          return newVal !== '' && newVal !== oldVal;
        });
        
        if (hasChanged) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[UTM] Capturing UTMs (URL + session):', mergedUtms);
          }
          return {
            utm_source: mergedUtms.utm_source || prev.utm_source || '(direct)',
            utm_medium: mergedUtms.utm_medium || prev.utm_medium || '(none)',
            utm_campaign: mergedUtms.utm_campaign || prev.utm_campaign || '',
            utm_content: mergedUtms.utm_content || prev.utm_content || '',
            utm_term: mergedUtms.utm_term || prev.utm_term || '',
            utm_id: mergedUtms.utm_id || prev.utm_id || '',
            gclid: mergedUtms.gclid || prev.gclid || ''
          };
        }
        return prev;
      });
    }
  }, []);

  // Capture on mount with delay to allow index.html script to run
  useEffect(() => {
    captureUTMsFromURL();
    
    const timer = setTimeout(() => {
      captureUTMsFromURL();
    }, 200);
    
    return () => clearTimeout(timer);
  }, [captureUTMsFromURL]);

  // Capture when URL changes (navigation)
  useEffect(() => {
    captureUTMsFromURL();
  }, [location.search, captureUTMsFromURL]);

  // Watch for URL changes via History API
  useEffect(() => {
    const handleUrlChange = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[UTM] URL change detected via popstate');
      }
      captureUTMsFromURL();
    };
    
    window.addEventListener('popstate', handleUrlChange);
    
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      handleUrlChange();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      handleUrlChange();
    };
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [captureUTMsFromURL]);

  // ============================================================
  // Expose UTMs globally for GTM.js
  // ============================================================
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const consentGranted = hasAnalyticsConsent();
      
      window.__utmContext = {
        getUtms: (includeGclid = false) => {
          const sessionData = getUTMsFromSession();
          
          const baseUtms = {
            utm_source: sessionData.utm_source || utmParams.utm_source || '',
            utm_medium: sessionData.utm_medium || utmParams.utm_medium || '',
            utm_campaign: sessionData.utm_campaign || utmParams.utm_campaign || '',
            utm_content: sessionData.utm_content || utmParams.utm_content || '',
            utm_term: sessionData.utm_term || utmParams.utm_term || '',
            utm_id: sessionData.utm_id || utmParams.utm_id || '',
          };
          
          // Get gclid from sessionStorage
          let gclidValue = sessionData.gclid;
          if (!gclidValue) {
            gclidValue = utmParams.gclid;
          }
          if (!gclidValue && typeof sessionStorage !== 'undefined') {
            gclidValue = sessionStorage.getItem('gclid') || '';
          }
          
          // ============================================================
          // gclid is included ONLY if:
          // 1. includeGclid is true AND consent is granted
          // ============================================================
          if (includeGclid && consentGranted && gclidValue) {
            return { ...baseUtms, gclid: gclidValue };
          }
          
          return baseUtms;
        },
        getUtmsForDebug: () => {
          const sessionData = getUTMsFromSession();
          
          const result = {
            utm_source: sessionData.utm_source || utmParams.utm_source || '',
            utm_medium: sessionData.utm_medium || utmParams.utm_medium || '',
            utm_campaign: sessionData.utm_campaign || utmParams.utm_campaign || '',
            utm_content: sessionData.utm_content || utmParams.utm_content || '',
            utm_term: sessionData.utm_term || utmParams.utm_term || '',
            utm_id: sessionData.utm_id || utmParams.utm_id || '',
          };
          
          // gclid is NEVER included in debug events
          return result;
        },
        available: Object.keys(utmParams).some(k => 
          utmParams[k] && utmParams[k] !== '(direct)' && utmParams[k] !== '(none)'
        ) || Object.keys(getUTMsFromSession()).length > 0,
        consentGranted
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[UTM] Exposed global reference for GTM.');
        console.log('[UTM] GCLID stored in sessionStorage (CRM/DB only):', sessionStorage.getItem('gclid'));
      }
    }
  }, [utmParams]);

  // Helper to check if UTMs are real tracking parameters
  const hasValidUTMs = utmParams.utm_source !== '(direct)' && 
                      utmParams.utm_medium !== '(none)';

  // Helper to add UTMs to a URL (for internal navigation)
  const addUTMsToUrl = useCallback((baseUrl, includeGclid = false) => {
    let includeGclidParam = includeGclid;
    if (typeof window !== 'undefined') {
      try {
        const savedConsent = localStorage.getItem('gtm_consent');
        if (savedConsent) {
          const consent = JSON.parse(savedConsent);
          includeGclidParam = consent.analytics === true;
        }
      } catch (e) {
        // Ignore
      }
    }
    
    const sessionData = getUTMsFromSession();
    const hasUtms = Object.keys(sessionData).length > 0 || hasValidUTMs;
    
    if (!hasUtms) return baseUrl;
    
    try {
      if (!baseUrl) return baseUrl;
      
      let pathAndQuery = baseUrl;
      if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
        const urlObj = new URL(baseUrl);
        pathAndQuery = urlObj.pathname + urlObj.search;
      }
      
      const url = new URL(pathAndQuery, window.location.origin);
      
      UTM_KEYS.forEach(key => {
        let value = utmParams[key];
        if (!value && typeof sessionStorage !== 'undefined') {
          value = sessionStorage.getItem(key) || '';
        }
        
        if (value && value !== '(direct)' && value !== '(none)' && value !== '') {
          if (!url.searchParams.get(key)) {
            url.searchParams.set(key, value);
          }
        }
      });
      
      // Add gclid separately if consent is granted
      if (includeGclidParam) {
        const gclidValue = sessionStorage.getItem('gclid') || utmParams.gclid || '';
        if (gclidValue) {
          url.searchParams.set('gclid', gclidValue);
        }
      }
      
      return url.pathname + url.search + url.hash;
    } catch (e) {
      console.error('[UTMContext] Error adding UTMs to URL:', e);
      return baseUrl;
    }
  }, [utmParams, hasValidUTMs]);
  
  // Helper to navigate with UTMs preserved in URL
  const navigateWithUTMs = useCallback((navigate, path) => {
    const urlWithUtms = addUTMsToUrl(path);
    navigate(urlWithUtms);
  }, [addUTMsToUrl]);

  // ============================================================
  // getUtmsForApi - For CRM/DB (includes gclid based on consent)
  // ============================================================
  const getUtmsForApi = useCallback((includeGclid = false) => {
    let includeGclidParam = includeGclid;
    if (typeof window !== 'undefined') {
      try {
        const savedConsent = localStorage.getItem('gtm_consent');
        if (savedConsent) {
          const consent = JSON.parse(savedConsent);
          includeGclidParam = consent.analytics === true;
        }
      } catch (e) {
        // Ignore
      }
    }
    
    const sessionData = getUTMsFromSession();
    
    const baseUtms = {
      utm_source: sessionData.utm_source || utmParams.utm_source || '(direct)',
      utm_medium: sessionData.utm_medium || utmParams.utm_medium || '(none)',
      utm_campaign: sessionData.utm_campaign || utmParams.utm_campaign || '',
      utm_content: sessionData.utm_content || utmParams.utm_content || '',
      utm_term: sessionData.utm_term || utmParams.utm_term || '',
      utm_id: sessionData.utm_id || utmParams.utm_id || '',
    };
    
    // Get gclid from sessionStorage
    let gclidValue = sessionData.gclid;
    if (!gclidValue) {
      gclidValue = utmParams.gclid;
    }
    if (!gclidValue && typeof sessionStorage !== 'undefined') {
      gclidValue = sessionStorage.getItem('gclid') || '';
    }
    
    // Include gclid ONLY if requested AND consent is granted
    if (includeGclidParam && gclidValue) {
      return { ...baseUtms, gclid: gclidValue };
    }
    
    return baseUtms;
  }, [utmParams]);

  // ============================================================
  // getUtmsForDataLayer - FOR GA4 EVENTS (includes gclid with consent)
  // This is used for events like clic_payer_reserver, purchase, etc.
  // ============================================================
  const getUtmsForDataLayer = useCallback(() => {
    const consentGranted = hasAnalyticsConsent();
    const sessionData = getUTMsFromSession();
    
    const baseUtms = {
      utm_source: sessionData.utm_source || utmParams.utm_source || '',
      utm_medium: sessionData.utm_medium || utmParams.utm_medium || '',
      utm_campaign: sessionData.utm_campaign || utmParams.utm_campaign || '',
      utm_content: sessionData.utm_content || utmParams.utm_content || '',
      utm_term: sessionData.utm_term || utmParams.utm_term || '',
      utm_id: sessionData.utm_id || utmParams.utm_id || '',
    };
    
    // Get gclid from sessionStorage
    let gclidValue = sessionData.gclid;
    if (!gclidValue) {
      gclidValue = utmParams.gclid;
    }
    if (!gclidValue && typeof sessionStorage !== 'undefined') {
      gclidValue = sessionStorage.getItem('gclid') || '';
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[UTM] getUtmsForDataLayer - consentGranted:', consentGranted);
      console.log('[UTM] getUtmsForDataLayer - gclidValue:', gclidValue);
    }
    
    // ============================================================
    // gclid is included ONLY if consent is granted
    // This is for GA4 events (clic_payer_reserver, purchase, etc.)
    // ============================================================
    if (consentGranted && gclidValue) {
      return { ...baseUtms, gclid: gclidValue };
    }
    
    return baseUtms;
  }, [utmParams]);

  // Get current UTMs for debugging
  const getDebugInfo = useCallback(() => {
    return {
      utmParams,
      hasValidUTMs,
      url: window.location.href,
      urlParams: getUTMsFromURL(),
      sessionUtms: getUTMsFromSession(),
      timestamp: new Date().toISOString(),
      consentGranted: hasAnalyticsConsent()
    };
  }, [utmParams, hasValidUTMs]);

  const value = {
    utmParams,
    hasValidUTMs,
    addUTMsToUrl,
    navigateWithUTMs,
    getUtmsForApi,
    getUtmsForDataLayer,
    getDebugInfo,
    updateUtmParams: (params) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[UTMContext] updateUtmParams is deprecated. UTMs are captured from URL.');
      }
      setUtmParams(prev => ({ ...prev, ...params }));
    },
    clearUtmParams: () => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[UTMContext] clearUtmParams is deprecated.');
      }
    }
  };

  return (
    <UTMContext.Provider value={value}>
      {children}
    </UTMContext.Provider>
  );
};

export const useUTM = () => {
  const context = useContext(UTMContext);
  if (!context) {
    throw new Error('useUTM must be used within a UTMProvider');
  }
  return context;
};

// Helper to get UTMs from global reference (for GTM and external scripts)
export const getUTMsFromGlobal = (includeGclid = false) => {
  if (typeof window !== 'undefined' && window.__utmContext && window.__utmContext.getUtms) {
    return window.__utmContext.getUtms(includeGclid);
  }
  return {};
};

// Helper to get UTMs for debug from global reference
export const getUTMsForDebugFromGlobal = () => {
  if (typeof window !== 'undefined' && window.__utmContext && window.__utmContext.getUtmsForDebug) {
    return window.__utmContext.getUtmsForDebug();
  }
  return {};
};

// ============================================================
// Helper: Get UTMs for CRM/DB (always includes gclid)
// ============================================================
export const getUTMsForCRM = () => {
  if (typeof sessionStorage === 'undefined') return {};
  
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'gclid'];
  const utms = {};
  
  keys.forEach(key => {
    const value = sessionStorage.getItem(key);
    if (value && value !== '' && value !== '(direct)' && value !== '(none)') {
      utms[key] = value;
    }
  });
  
  return utms;
};